/**
 * Unified Job API Service
 * Combines multiple job APIs (JSearch + Google Jobs + Adzuna) for comprehensive results
 */

import { 
  searchJSearch, 
  isAPIConfigured as isJSearchConfigured 
} from './jobSearchAPI';

import { 
  searchGoogleJobs, 
  isGoogleJobsAPIConfigured 
} from './googleJobsAPI';

// ⬇️ NEW IMPORT: Adzuna API
import { 
  searchAdzuna, 
  isAdzunaConfigured 
} from './adzunaAPI';
// ⬆️ NEW IMPORT ⬆️

/**
 * Search for ALL types of jobs across all available APIs
 * @param {Object} params - Search parameters
 * @returns {Promise<Object>} Combined job results from all sources
 */
export async function searchAllJobs(params = {}) {
  const {
    query = 'engineering jobs', // Default query
    location = 'United Kingdom',
    remote = false,
    page = 1,
  } = params;

  const results = {
    success: true,
    jobs: [],
    sources: {
      jsearch: { enabled: false, count: 0, error: null },
      googleJobs: { enabled: false, count: 0, error: null },
      adzuna: { enabled: false, count: 0, error: null }, // ✅ Adzuna added
    },
    total: 0,
  };

  const searchPromises = [];

  // --- JSearch ---
  if (isJSearchConfigured()) {
    results.sources.jsearch.enabled = true;
    searchPromises.push(
      searchJSearch({ query, location, page, remote_jobs_only: remote })
        .then(res => {
          if (res.success) {
            const jobs = res.jobs.map(j => ({ ...j, dataSource: 'JSearch' }));
            results.jobs.push(...jobs);
            results.sources.jsearch.count = jobs.length;
          } else {
            results.sources.jsearch.error = res.error;
          }
        })
        .catch(err => (results.sources.jsearch.error = err.message))
    );
  }

  // --- Google Jobs ---
  if (isGoogleJobsAPIConfigured()) {
    results.sources.googleJobs.enabled = true;
    searchPromises.push(
      searchGoogleJobs({ query, location, remoteOnly: remote, page })
        .then(res => {
          if (res.success) {
            const jobs = res.jobs.map(j => ({ ...j, dataSource: 'Google Jobs' }));
            results.jobs.push(...jobs);
            results.sources.googleJobs.count = jobs.length;
          } else {
            results.sources.googleJobs.error = res.error;
          }
        })
        .catch(err => (results.sources.googleJobs.error = err.message))
    );
  }

  // --- Adzuna (This was missing from your file) ---
  if (isAdzunaConfigured()) {
    results.sources.adzuna.enabled = true;
    searchPromises.push(
      searchAdzuna({ query, location, page, remote })
        .then(res => {
          if (res.success) { // Check if Adzuna call was successful
            const jobs = res.jobs.map(j => ({ ...j, dataSource: 'Adzuna' }));
            results.jobs.push(...jobs);
            results.sources.adzuna.count = jobs.length;
          } else {
            results.sources.adzuna.error = res.error;
          }
        })
        .catch(err => (results.sources.adzuna.error = err.message))
    );
  }

  // Wait for all
  await Promise.allSettled(searchPromises); // Use allSettled to prevent one failure from stopping others

  
  // Remove duplicates based on title and company
  results.jobs = removeDuplicateJobs(results.jobs);
  results.total = results.jobs.length;

  // Sort by match score (highest first)
  results.jobs.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));

  return results;
}

/**
 * Search for specific job type
 * @param {string} jobType - 'fulltime', 'parttime', 'contract', 'internship'
 * @param {Object} params - Additional search parameters
 */
export async function searchJobsByType(jobType, params = {}) {
  const typeQueries = {
    fulltime: 'full-time',
    parttime: 'part-time',
    contract: 'contract freelance',
    internship: 'internship',
  };

  const typeQuery = typeQueries[jobType.toLowerCase()] || '';
  const baseQuery = params.query || 'jobs';

  return searchAllJobs({
    ...params,
    query: `${typeQuery} ${baseQuery}`.trim(),
  });
}

/**
 * Remove duplicate jobs based on title and company similarity
 */
function removeDuplicateJobs(jobs) {
  const seen = new Map();
  const unique = [];

  for (const job of jobs) {
    // Create a key based on normalized title and company
    const title = (job.title || '').toLowerCase().trim();
    const company = (job.client || job.company || '').toLowerCase().trim();
    const key = `${title}-${company}`;

    if (!seen.has(key)) {
      seen.set(key, true);
      unique.push(job);
    }
  }

  return unique;
}

/**
 * Check which APIs are configured
 */
export function getConfiguredAPIs() {
  const jsearch = isJSearchConfigured();
  const googleJobs = isGoogleJobsAPIConfigured();
  const adzuna = isAdzunaConfigured();
  
  return {
    jsearch,
    googleJobs,
    adzuna,
    total: (jsearch ? 1 : 0) + (googleJobs ? 1 : 0) + (adzuna ? 1 : 0),
  };
}

/**
 * Get API status and statistics
 */
export function getAPIStatus() {
  const configured = getConfiguredAPIs();
  
  return {
    configured: configured.total,
    available: 3, // ✅ FIX: Total available APIs is 3
    apis: {
      jsearch: {
        name: 'JSearch (RapidAPI)',
        configured: configured.jsearch,
        sources: ['LinkedIn', 'Indeed', 'Glassdoor', 'ZipRecruiter'],
        freeTier: '500 requests/month',
        jobTypes: ['Full-Time', 'Part-Time', 'Contract', 'Internship'],
      },
      googleJobs: {
        name: 'Google Jobs (SerpApi)',
        configured: configured.googleJobs,
        sources: ['Google for Jobs', 'LinkedIn', 'Indeed', 'Monster'],
        freeTier: '250 searches/month',
        jobTypes: ['Full-Time', 'Part-Time', 'Contract', 'Internship'],
      },
      // ✅ Adzuna status was missing from your file
      adzuna: {
        name: 'Adzuna API',
        configured: configured.adzuna,
        sources: ['Various Job Boards, Direct Employers'],
        freeTier: 'Varies by plan',
        jobTypes: ['Full-Time', 'Part-Time', 'Contract', 'Temporary'], 
      },
    },
  };
}

/**
 * Get job statistics by type from results
 */
export function getJobTypeStatistics(jobs) {
  const stats = {
    fulltime: 0,
    parttime: 0,
    contract: 0,
    internship: 0,
    other: 0,
  };

  jobs.forEach(job => {
    const type = (job.jobType || job.type || '').toLowerCase();
    
    if (type.includes('full')) {
      stats.fulltime++;
    } else if (type.includes('part')) {
      stats.parttime++;
    } else if (type.includes('contract') || type.includes('freelance')) {
      stats.contract++;
    } else if (type.includes('intern') || type.includes('temp')) { // Added 'temp' for Adzuna
      stats.internship++;
    } else {
      stats.other++;
    }
  });

  return stats;
}

export default {
  searchAllJobs,
  searchJobsByType,
  getConfiguredAPIs,
  getAPIStatus,
  getJobTypeStatistics,
};